import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';

type Categoria = { id: number; nombre: string; icono: string; area: string; descripcion: string };
type Barrio    = { id: number; nombre: string };

async function reverseGeocodificar(lat: number, lng: number): Promise<{ calle: string; nro: string; localidad: string } | null> {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      { headers: { 'User-Agent': 'CiudadApp/1.0 soporte.tecnico@ciudadapp.com.ar' } }
    );
    const d = await r.json();
    if (d?.address) {
      return {
        calle:     d.address.road || d.address.pedestrian || '',
        nro:       d.address.house_number || '',
        localidad: d.address.city || d.address.town || d.address.village || d.address.municipality || '',
      };
    }
  } catch (_) {}
  return null;
}

function crearPinIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="width:28px;height:28px;border-radius:50%;background:#2563eb;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;">
        <div style="width:8px;height:8px;border-radius:50%;background:white;"></div>
      </div>
      <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:10px solid #2563eb;"></div>
    </div>`,
    iconSize: [28, 38],
    iconAnchor: [14, 38],
  });
}

export default function NuevoReclamo() {
  const nav       = useNavigate();
  const ciudadano = useAuth((s) => s.ciudadano);
  const mid       = ciudadano?.municipio_id ?? 0;

  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [barrios,    setBarrios]    = useState<Barrio[]>([]);
  const [catModal,   setCatModal]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [err,        setErr]        = useState<string | null>(null);
  const [gpsMsg,     setGpsMsg]     = useState<string | null>(null);
  const [fotos,      setFotos]      = useState<File[]>([]);
  const [previews,   setPreviews]   = useState<string[]>([]);
  const [mapOpen,      setMapOpen]      = useState(false);
  const [mapGpsMsg,    setMapGpsMsg]    = useState<string | null>(null);
  const [uploadStatus,      setUploadStatus]      = useState<string | null>(null);
  const [geocodedLocalidad, setGeocodedLocalidad] = useState('');
  const [mapHint,    setMapHint]    = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mapDivRef    = useRef<HTMLDivElement>(null);
  const leafletMap   = useRef<L.Map | null>(null);
  const markerRef    = useRef<L.Marker | null>(null);
  const mapInitPos   = useRef<[number, number] | null>(null);

  const [form, setForm] = useState({
    categoria_id:  null as number | null,
    categoriaNoSe: false,
    titulo:        '',
    descripcion:   '',
    calle:         '',
    nro_puerta:    '',
    barrio:        '',
    latitud:       null as number | null,
    longitud:      null as number | null,
  });

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!mid) return;
    api<Categoria[]>(`/categorias?municipio_id=${mid}`, { auth: false })
      .then((d) => setCategorias([...d].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))))
      .catch(() => {});
    api<Barrio[]>(`/barrios?municipio_id=${mid}`, { auth: false })
      .then(setBarrios)
      .catch(() => {});
  }, [mid]);

  // ── Mapa Leaflet inline ────────────────────────────────────
  useEffect(() => {
    if (!mapOpen) return;
    let map: L.Map | null = null;

    const timer = setTimeout(() => {
      if (!mapDivRef.current) return;

      const [lat, lng] = mapInitPos.current ?? [-34.6, -64.0];
      const zoom = mapInitPos.current ? 16 : 6;

      map = L.map(mapDivRef.current, { center: [lat, lng], zoom, zoomControl: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      const icon = crearPinIcon();

      // Si ya había coordenadas, colocar pin inicial
      if (mapInitPos.current) {
        const m = L.marker([lat, lng], { draggable: true, icon }).addTo(map);
        markerRef.current = m;
        setMapHint(false);
      }

      // Toque en el mapa: colocar o mover el pin
      map.on('click', (e: L.LeafletMouseEvent) => {
        if (markerRef.current) {
          markerRef.current.setLatLng(e.latlng);
        } else {
          const m = L.marker(e.latlng, { draggable: true, icon }).addTo(map!);
          markerRef.current = m;
        }
        setMapHint(false);
      });

      setTimeout(() => map?.invalidateSize(), 150);
      leafletMap.current = map;

      // Sin posición previa: intentar GPS silenciosamente para centrar el mapa
      if (!mapInitPos.current && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!map) return;
            map.setView([pos.coords.latitude, pos.coords.longitude], 15);
          },
          () => {},
          { timeout: 8000, maximumAge: 60000 },
        );
      }
    }, 80);

    return () => {
      clearTimeout(timer);
      map?.remove();
      leafletMap.current = null;
      markerRef.current = null;
    };
  }, [mapOpen]);

  function abrirMapa() {
    mapInitPos.current = form.latitud ? [form.latitud, form.longitud!] : null;
    setMapHint(true);
    setMapOpen(true);
  }

  function usarGpsEnMapa() {
    if (!navigator.geolocation || !leafletMap.current) return;
    setMapGpsMsg('Obteniendo…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = L.latLng(pos.coords.latitude, pos.coords.longitude);
        leafletMap.current!.setView(latlng, 17);
        if (markerRef.current) {
          markerRef.current.setLatLng(latlng);
        } else {
          markerRef.current = L.marker(latlng, { draggable: true, icon: crearPinIcon() })
            .addTo(leafletMap.current!);
        }
        setMapHint(false);
        setMapGpsMsg(null);
      },
      () => setMapGpsMsg(null),
      { timeout: 10000 }
    );
  }

  function confirmarMapa() {
    if (!markerRef.current) return;
    const { lat, lng } = markerRef.current.getLatLng();
    setMapOpen(false);
    set('latitud', lat);
    set('longitud', lng);
    reverseGeocodificar(lat, lng).then((dir) => {
      if (dir?.calle) {
        set('calle', dir.calle);
        if (!form.nro_puerta) set('nro_puerta', dir.nro);
      }
      if (dir?.localidad) setGeocodedLocalidad(dir.localidad);
    });
  }

  // ── GPS rápido ─────────────────────────────────────────────
  async function obtenerGps() {
    if (!navigator.geolocation) return setErr('GPS no disponible');
    setGpsMsg('Obteniendo ubicación…');
    setErr(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        set('latitud', lat);
        set('longitud', lng);
        setGpsMsg('Buscando dirección…');
        const dir = await reverseGeocodificar(lat, lng);
        if (dir?.calle) {
          set('calle', dir.calle);
          if (!form.nro_puerta) set('nro_puerta', dir.nro);
        }
        if (dir?.localidad) setGeocodedLocalidad(dir.localidad);
        setGpsMsg(null);
      },
      () => { setGpsMsg(null); setErr('No se pudo obtener el GPS. Habilitá la ubicación.'); },
      { timeout: 10000, maximumAge: 15000 }
    );
  }

  // ── Fotos ──────────────────────────────────────────────────
  function agregarFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || fotos.length >= 3) return;
    setFotos((f) => [...f, file]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreviews((p) => [...p, ev.target!.result as string]);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function quitarFoto(idx: number) {
    setFotos((f) => f.filter((_, i) => i !== idx));
    setPreviews((p) => p.filter((_, i) => i !== idx));
  }

  // ── Enviar ─────────────────────────────────────────────────
  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!form.categoria_id && !form.categoriaNoSe) {
      return setErr('Seleccioná el tipo de problema o elegí "No sé / Otro".');
    }
    if (!form.titulo.trim())      return setErr('Ingresá un título');
    if (!form.descripcion.trim()) return setErr('Describí el problema');

    setLoading(true);
    setErr(null);
    try {
      const body = {
        categoria_id: form.categoria_id,
        titulo:       form.titulo.trim(),
        descripcion:  form.descripcion.trim(),
        calle:        form.calle.trim(),
        nro_puerta:   form.nro_puerta.trim(),
        barrio:       form.barrio || '',
        latitud:      form.latitud,
        longitud:     form.longitud,
      };
      const res = await api<{ id: number; nro_expediente: string }>('/reclamos', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      let errFotos = 0;
      for (let i = 0; i < fotos.length; i++) {
        setUploadStatus(`Subiendo foto ${i + 1} de ${fotos.length}…`);
        try {
          const fd = new FormData();
          fd.append('foto', fotos[i]);
          await api(`/reclamos?action=imagen&id=${res.id}`, { method: 'POST', body: fd });
        } catch (e: any) {
          errFotos++;
          console.error(`Error foto ${i + 1}:`, e?.message ?? e);
        }
      }
      setUploadStatus(null);
      if (errFotos > 0) {
        setErr(`Reclamo creado (#${res.nro_expediente}), pero ${errFotos} foto(s) no se pudieron subir.`);
        setLoading(false);
        return;
      }

      nav('/reclamos', { replace: true, state: { nuevo: res.nro_expediente } });
    } catch (e: any) {
      setErr(e.message || 'Error al enviar el reclamo');
    } finally {
      setLoading(false);
    }
  }

  // ── Agrupación categorías ───────────────────────────────────
  const areaMap: Record<string, Categoria[]> = {};
  for (const c of categorias) {
    const k = c.area || 'Sin área';
    if (!areaMap[k]) areaMap[k] = [];
    areaMap[k].push(c);
  }
  const areas = Object.keys(areaMap).sort((a, b) => a.localeCompare(b, 'es'));
  const catSel = categorias.find((c) => c.id === form.categoria_id);

  return (
    <>
      <TopBar title="Nuevo reclamo" back />

      {/* ── Modal categorías (bottom sheet) ────────────────── */}
      {catModal && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setCatModal(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl max-h-[78vh] flex flex-col shadow-2xl">
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
              <div>
                <p className="font-extrabold text-gray-900 text-sm">¿Qué tipo de problema es?</p>
                <p className="text-gray-500 text-xs mt-0.5">Elegí la opción más parecida</p>
              </div>
              <button
                onClick={() => setCatModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-sm font-bold flex-shrink-0"
              >✕</button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 pb-8">
              <button
                onClick={() => { set('categoriaNoSe', true); set('categoria_id', null); setCatModal(false); }}
                className={`w-full flex items-center gap-3 py-3.5 border-b border-gray-100 text-left ${form.categoriaNoSe ? 'text-brand-600' : 'text-gray-800'}`}
              >
                <span className="text-xl w-7 text-center">❓</span>
                <div className="flex-1">
                  <p className="font-semibold text-sm">No sé / Otro</p>
                  <p className="text-xs text-gray-400">No encontré mi caso o no sé a qué área corresponde.</p>
                </div>
                {form.categoriaNoSe && <span className="text-brand-600">✓</span>}
              </button>
              {areas.map((area) => (
                <div key={area}>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest pt-4 pb-1">{area}</p>
                  {areaMap[area].map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { set('categoria_id', c.id); set('categoriaNoSe', false); setCatModal(false); }}
                      className={`w-full flex items-center gap-3 py-3 border-b border-gray-100 text-left ${form.categoria_id === c.id ? 'text-brand-600' : 'text-gray-800'}`}
                    >
                      <span className="text-xl w-7 text-center">{c.icono || '📋'}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{c.nombre}</p>
                        {c.descripcion && <p className="text-xs text-gray-400 line-clamp-1">{c.descripcion}</p>}
                      </div>
                      {form.categoria_id === c.id && <span className="text-brand-600">✓</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <form onSubmit={enviar}>
        <main className="px-4 py-5 space-y-5 max-w-md mx-auto pb-28">

          {/* Categoría */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
              Categoría <span className="text-red-500">*</span>
            </p>
            {catSel ? (
              <button type="button" onClick={() => setCatModal(true)}
                className="w-full flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-left">
                <span className="text-2xl">{catSel.icono || '📋'}</span>
                <div className="flex-1">
                  <p className="font-bold text-sm text-gray-900">{catSel.nombre}</p>
                  {catSel.area && <p className="text-xs text-gray-500">→ {catSel.area}</p>}
                </div>
                <span className="text-brand-600 text-sm font-semibold">Cambiar ›</span>
              </button>
            ) : form.categoriaNoSe ? (
              <button type="button" onClick={() => setCatModal(true)}
                className="w-full flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-left">
                <span className="text-2xl">❓</span>
                <div className="flex-1">
                  <p className="font-bold text-sm text-gray-900">No sé / Otro</p>
                  <p className="text-xs text-gray-500">Se deriva a Mesa de Entrada</p>
                </div>
                <span className="text-brand-600 text-sm font-semibold">Cambiar ›</span>
              </button>
            ) : (
              <button type="button" onClick={() => setCatModal(true)}
                className="w-full flex items-center gap-3 bg-white border border-app rounded-xl px-4 py-3 text-left">
                <span className="text-xl">📋</span>
                <span className="flex-1 text-gray-400 text-sm">Seleccioná el tipo de problema…</span>
                <span className="text-gray-400">›</span>
              </button>
            )}
          </div>

          {/* Detalle */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Detalle</p>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.titulo}
                onChange={(e) => set('titulo', e.target.value)}
                placeholder="Ej: Bache en Av. San Martín"
                className="w-full bg-white border border-app rounded-xl px-4 py-3 text-sm text-gray-900 focus:border-brand-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Descripción <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.descripcion}
                onChange={(e) => set('descripcion', e.target.value)}
                placeholder="Describí el problema con el mayor detalle posible…"
                rows={4}
                className="w-full bg-white border border-app rounded-xl px-4 py-3 text-sm text-gray-900 focus:border-brand-500 outline-none resize-none"
              />
            </div>
          </div>

          {/* Ubicación */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Ubicación del problema</p>
            <p className="text-xs text-gray-400 -mt-2">Podés escribir la dirección, usar el mapa o el GPS.</p>

            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Calle</label>
                <input
                  type="text"
                  value={form.calle}
                  onChange={(e) => set('calle', e.target.value)}
                  placeholder="Av. San Martín"
                  className="w-full bg-white border border-app rounded-xl px-3 py-2.5 text-sm text-gray-900 focus:border-brand-500 outline-none"
                />
              </div>
              <div className="w-20">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nro.</label>
                <input
                  type="text"
                  value={form.nro_puerta}
                  onChange={(e) => set('nro_puerta', e.target.value)}
                  placeholder="1200"
                  className="w-full bg-white border border-app rounded-xl px-3 py-2.5 text-sm text-gray-900 focus:border-brand-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Barrio <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              {barrios.length > 0 ? (
                <select
                  value={form.barrio}
                  onChange={(e) => set('barrio', e.target.value)}
                  className="w-full bg-white border border-app rounded-xl px-3 py-2.5 text-sm text-gray-900 focus:border-brand-500 outline-none"
                >
                  <option value="">Sin barrio específico</option>
                  {barrios.map((b) => (
                    <option key={b.id} value={b.nombre}>{b.nombre}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={form.barrio}
                  onChange={(e) => set('barrio', e.target.value)}
                  placeholder="Ej: Centro, San Martín…"
                  className="w-full bg-white border border-app rounded-xl px-3 py-2.5 text-sm text-gray-900 focus:border-brand-500 outline-none"
                />
              )}
            </div>

            {/* Chip de ubicación confirmada */}
            {form.latitud && !mapOpen && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-green-700 font-bold text-xs">📍 Ubicación marcada</p>
                  <p className="text-green-600 text-xs mt-0.5">
                    {form.calle
                      ? `Calle: ${[form.calle, form.nro_puerta].filter(Boolean).join(' ')}${geocodedLocalidad ? ` · ${geocodedLocalidad}` : ''}`
                      : `${form.latitud.toFixed(5)}, ${form.longitud?.toFixed(5)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <button type="button" onClick={abrirMapa}
                    className="text-blue-600 text-xs font-semibold">Editar</button>
                  <button type="button"
                    onClick={() => { set('latitud', null); set('longitud', null); set('calle', ''); set('nro_puerta', ''); }}
                    className="text-red-500 text-xs font-semibold">Quitar</button>
                </div>
              </div>
            )}

            {/* Botones para abrir mapa / GPS rápido */}
            {!form.latitud && !mapOpen && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={abrirMapa}
                  className="flex-1 flex items-center justify-center gap-2 bg-white border border-app rounded-xl py-3 text-sm text-gray-700 font-semibold"
                >
                  🗺️ Ubicar en el mapa
                </button>
                <button
                  type="button"
                  onClick={obtenerGps}
                  disabled={!!gpsMsg}
                  className="flex items-center justify-center gap-1.5 px-4 bg-white border border-app rounded-xl py-3 text-sm text-gray-700 font-semibold disabled:opacity-50 whitespace-nowrap"
                >
                  {gpsMsg ? '⏳' : '📍 GPS'}
                </button>
              </div>
            )}

            {/* ── Mapa inline ──────────────────────────────── */}
            {mapOpen && (
              <div className="rounded-xl overflow-hidden border border-app shadow-sm">
                {/* Instrucción */}
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <p className="text-xs text-gray-600">
                    {mapHint ? 'Tocá el mapa para marcar la ubicación del problema' : 'Podés mover el alfiler arrastrándolo'}
                  </p>
                  <button
                    type="button"
                    onClick={usarGpsEnMapa}
                    disabled={!!mapGpsMsg}
                    className="flex items-center gap-1 text-xs text-blue-600 font-semibold disabled:opacity-50 ml-2 whitespace-nowrap"
                  >
                    {mapGpsMsg ? '⏳' : '📍 Mi ubicación'}
                  </button>
                </div>

                {/* Contenedor del mapa con alto fijo */}
                <div style={{ position: 'relative', height: 260 }}>
                  <div ref={mapDivRef} style={{ position: 'absolute', inset: 0 }} />
                  {/* Hint overlay hasta que se ponga el pin */}
                  {mapHint && (
                    <div style={{
                      position: 'absolute', inset: 0, zIndex: 500,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                      <div style={{
                        background: 'rgba(0,0,0,0.55)', borderRadius: 10,
                        padding: '8px 14px',
                      }}>
                        <p style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>
                          Tocá para colocar el alfiler
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botones Cancelar / Confirmar */}
                <div className="flex gap-2 p-3 bg-white border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setMapOpen(false)}
                    className="flex-1 border border-app rounded-xl py-2.5 text-sm text-gray-600 font-semibold"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmarMapa}
                    disabled={!markerRef.current}
                    className="flex-1 bg-blue-600 disabled:opacity-40 text-white rounded-xl py-2.5 text-sm font-bold"
                  >
                    Confirmar ubicación
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Fotos */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
              Fotos adjuntas (máx. 3)
            </p>
            <div className="flex gap-3 flex-wrap">
              {previews.map((url, i) => (
                <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-app">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => quitarFoto(i)}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full text-white text-xs flex items-center justify-center"
                  >✕</button>
                </div>
              ))}
              {fotos.length < 3 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400"
                >
                  <span className="text-2xl leading-none">+</span>
                  <span className="text-xs mt-1">Agregar</span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={agregarFoto}
            />
          </div>

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-4 font-bold text-base"
          >
            {uploadStatus ?? (loading ? 'Creando reclamo…' : '📤 Enviar reclamo')}
          </button>
        </main>
      </form>
    </>
  );
}
